<script>
// Article suggestions (title, TTC price, stock status) under the catalogue search field, instances with Saisie rapide only.
// The field itself is untouched: Enter still runs the full search, unless a
// suggestion is highlighted with the arrow keys; picking one emits "pick" (the page then searches that article).
import { debounce } from "lodash";
import ItemsApi from "@/services/items-api";
import CatalogStockStatus from "@/components/CatalogStockStatus";

export default {
  name: "CatalogSearchSuggestions",
  components: { CatalogStockStatus },
  props: {
    // the catalogue search text (v-model of the field)
    query: { type: String, default: "" },
    // id of the search <input>
    inputId: { type: String, required: true },
    // text => Promise<items>; default: the catalogue search (/api/items/list, 6 articles)
    lookup: { type: Function, default: null },
    // what follows the title: "price-stock" (catalogue) or "ref-barcode" (Recherche de mouvements)
    details: { type: String, default: "price-stock" },
  },

  data() {
    return {
      items: [],
      open: false,
      loading: false,
      highlighted: -1,
      lastPicked: null,
      consumedEnter: false,
    };
  },

  created() {
    this.debouncedSearch = debounce(this.search, 300);
  },

  mounted() {
    this.onKeydown = this.onKeydown.bind(this);
    this.onKeyupCapture = this.onKeyupCapture.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onFocus = this.onFocus.bind(this);
    document.addEventListener("keydown", this.onKeydown, true);
    // capture phase on document: runs before the field's own @keyup.enter, so a picked suggestion is not searched twice
    document.addEventListener("keyup", this.onKeyupCapture, true);
    document.addEventListener("mousedown", this.onDocumentClick, true);
    document.addEventListener("focusin", this.onFocus, true);
  },

  beforeDestroy() {
    document.removeEventListener("keydown", this.onKeydown, true);
    document.removeEventListener("keyup", this.onKeyupCapture, true);
    document.removeEventListener("mousedown", this.onDocumentClick, true);
    document.removeEventListener("focusin", this.onFocus, true);
    this.debouncedSearch.cancel();
  },

  watch: {
    query(text) {
      // the page writes the picked ref into the field: no new suggestions for it
      if (this.lastPicked !== null && text === this.lastPicked) return;
      this.lastPicked = null;
      if (!text || text.trim().length < 2 || !this.isInputFocused()) {
        this.close();
        this.debouncedSearch.cancel();
        return;
      }
      this.loading = true;
      this.open = true;
      this.debouncedSearch();
    },
  },

  methods: {
    isInputFocused() {
      const input = document.getElementById(this.inputId);
      return !!input && document.activeElement === input;
    },

    search() {
      const text = this.query;
      if (!text || text.trim().length < 2) return;
      const request = this.lookup ? this.lookup(text) : ItemsApi.findItems(1, 6, [], text,
        null, null, false, null, null, null, null, null, null,
        null, false, false, false, false, false, false, false,
        new Date().getTime()).then(result => (result && result.list) || []);
      request
        .then(list => {
          if (text !== this.query) return;
          this.items = list || [];
          this.highlighted = -1;
        })
        .finally(() => { if (text === this.query) this.loading = false; });
    },

    close() {
      this.open = false;
      this.highlighted = -1;
    },

    pick(item) {
      if (!item) return;
      this.lastPicked = item.ref || "";
      this.close();
      this.$emit("pick", item);
    },

    onKeydown(event) {
      if (!this.open || event.target.id !== this.inputId) return;
      if (event.key === "ArrowDown" && this.items.length) {
        event.preventDefault();
        this.highlighted = (this.highlighted + 1) % this.items.length;
        this.scrollToHighlighted();
      } else if (event.key === "ArrowUp" && this.items.length) {
        event.preventDefault();
        this.highlighted = this.highlighted <= 0 ? this.items.length - 1 : this.highlighted - 1;
        this.scrollToHighlighted();
      } else if (event.key === "Escape") {
        this.close();
      } else if (event.key === "Enter") {
        if (this.highlighted >= 0 && this.items[this.highlighted]) {
          event.preventDefault();
          this.consumedEnter = true;
          this.pick(this.items[this.highlighted]);
        } else {
          // plain Enter: the page runs its full search as before
          this.close();
          this.debouncedSearch.cancel();
        }
      }
    },

    onKeyupCapture(event) {
      if (event.key === "Enter" && this.consumedEnter && event.target.id === this.inputId) {
        event.stopPropagation();
        this.consumedEnter = false;
      }
    },

    onDocumentClick(event) {
      if (!this.open) return;
      const input = document.getElementById(this.inputId);
      if (this.$el.contains(event.target) || (input && input.contains(event.target))) return;
      this.close();
    },

    onFocus(event) {
      if (event.target.id === this.inputId && this.items.length && this.query && this.query.trim().length >= 2
          && this.lastPicked === null) {
        this.open = true;
      }
    },

    scrollToHighlighted() {
      this.$nextTick(() => {
        const el = this.$el.querySelector(".cat-suggest__item--active");
        if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
      });
    },
  },
};
</script>

<template>
  <div v-show="open" class="cat-suggest" role="listbox">
    <div v-if="loading && !items.length" class="cat-suggest__status"><b-spinner small class="mr-2" />Recherche…</div>
    <div v-else-if="!items.length" class="cat-suggest__status">Aucun article trouvé. Appuyez sur Entrée pour une recherche complète.</div>
    <template v-else>
      <div
        v-for="(item, i) in items"
        :key="item.id || i"
        class="cat-suggest__item"
        :class="{ 'cat-suggest__item--active': i === highlighted }"
        role="option"
        :aria-selected="i === highlighted"
        @mouseenter="highlighted = i"
        @mousedown.prevent="pick(item)"
      >
        <span class="cat-suggest__title" :title="item.title">{{ item.title }}</span>
        <template v-if="details === 'ref-barcode'">
          <span class="cat-suggest__code">{{ item.ref }}</span>
          <span class="cat-suggest__code cat-suggest__code--barcode">{{ item.barcode || '' }}</span>
        </template>
        <template v-else>
          <span class="cat-suggest__price">{{ item.priceTaxIncl ? item.priceTaxIncl + ' TTC' : '' }}</span>
          <span class="cat-suggest__stock"><CatalogStockStatus :item="item" :value="item.stockStatus" /></span>
        </template>
      </div>
      <div class="cat-suggest__hint">↑ ↓ pour choisir, Entrée pour ouvrir l'article · Entrée sans sélection : recherche complète</div>
    </template>
  </div>
</template>

<style>
.cat-suggest {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1020;
  margin-top: 4px;
  /* short: the catalogue grid under it stays visible */
  max-height: 240px;
  overflow-y: auto;
  padding: .25rem;
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  box-shadow: 0 12px 24px -6px rgba(16, 24, 40, .16), 0 4px 8px -2px rgba(16, 24, 40, .08);
  text-align: left;
  text-transform: none;
}
.cat-suggest__item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: .4rem .6rem;
  border-radius: 8px;
  cursor: pointer;
}
.cat-suggest__title {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13.5px;
  font-weight: 600;
  color: #101828;
}
.cat-suggest__price {
  flex: 0 0 auto;
  min-width: 80px;
  text-align: right;
  font-size: 13px;
  font-weight: 600;
  color: #344054;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.cat-suggest__code {
  flex: 0 0 auto;
  min-width: 70px;
  text-align: right;
  font-size: 12.5px;
  color: #475467;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.cat-suggest__code--barcode {
  min-width: 120px;
  color: #98a2b3;
}
.cat-suggest__stock {
  flex: 0 0 56px;
  display: flex;
  justify-content: center;
}
.cat-suggest__stock .font-size-18,
.cat-suggest__stock .font-size-20 {
  font-size: 15px !important;
}
.cat-suggest__stock .font-size-14,
.cat-suggest__stock .font-size-16 {
  font-size: 12.5px !important;
}
.cat-suggest__item--active {
  background: #eef2fb;
}
.cat-suggest__status {
  padding: .6rem .5rem;
  color: #667085;
  font-size: 13px;
}
.cat-suggest__hint {
  position: sticky;
  bottom: -.25rem;
  padding: .3rem .5rem;
  background: #fff;
  border-top: 1px solid #f2f4f7;
  color: #98a2b3;
  font-size: 11.5px;
}
</style>
